import { ArrowRight, Globe } from 'lucide-react';
import { _t } from '@/i18n/t';
import { formatRunDate, hostFromUrl } from '../utils/companyIdentity';

interface Props {
  companyName: string;
  websiteUrl: string;
  onboardedOn?: string;
  onOpenWorkspace: () => void;
  onUseDifferentLink: () => void;
}

/**
 * A firm that already onboarded this website is not an error to recover from —
 * the work it is asking for is done. The screen says so, makes the existing
 * workspace concrete, and keeps the way back to a fresh link one tap away.
 */
export default function DuplicateWorkspacePanel({
  companyName,
  websiteUrl,
  onboardedOn,
  onOpenWorkspace,
  onUseDifferentLink,
}: Props) {
  const host = hostFromUrl(websiteUrl);
  const onboardedDate = formatRunDate(onboardedOn);

  return (
    <div className="ob-dupe" data-testid="onboarding-duplicate">
      <h1 className="ob-dupe__head text-foreground">{_t('onboarding.duplicate.head')}</h1>

      <p className="ob-dupe__body text-muted-foreground">
        {_t('onboarding.duplicate.body', { company: companyName, host })}
      </p>

      <div className="ob-dupe__card bg-card">
        <div className="ob-dupe__card-id">
          <span className="ob-dupe__favicon text-muted-foreground" aria-hidden="true">
            <Globe className="h-5 w-5" strokeWidth={1.6} />
          </span>
          <span className="ob-dupe__names">
            <span className="ob-dupe__name text-foreground" data-testid="onboarding-duplicate-name">{companyName}</span>
            <span className="ob-dupe__host text-muted-foreground">{host}</span>
          </span>
          <span className="ob-dupe__active text-[hsl(var(--text-muted))]">
            <i className="bg-status-published" aria-hidden="true" />
            {_t('onboarding.duplicate.active')}
          </span>
        </div>
        {onboardedDate && (
          <div className="ob-dupe__card-foot">
            <span className="ob-dupe__meta text-muted-foreground">
              {_t('onboarding.duplicate.onboardedOn', { date: onboardedDate })}
            </span>
          </div>
        )}
      </div>

      <div className="ob-dupe__actions">
        <button
          type="button"
          data-th-button={true}
          data-testid="onboarding-duplicate-open"
          className="ob-dupe__primary bg-foreground text-background hover:bg-foreground/90"
          onClick={onOpenWorkspace}
        >
          <span>{_t('onboarding.duplicate.open')}</span>
          <ArrowRight className="h-4 w-4" strokeWidth={1.9} aria-hidden="true" />
        </button>
        <button
          type="button"
          data-testid="onboarding-duplicate-different"
          className="ob-dupe__escape text-muted-foreground hover:text-foreground"
          onClick={onUseDifferentLink}
        >
          {_t('onboarding.duplicate.different')}
        </button>
      </div>

      <p className="ob-dupe__reassure text-[hsl(var(--text-muted))]">
        <strong className="font-medium text-muted-foreground">{_t('onboarding.duplicate.reassureLabel')}</strong>{' '}
        {_t('onboarding.duplicate.reassure')}
      </p>
    </div>
  );
}
